import { useMutation, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import useGraphQLClient from '../../../app/hooks/useGraphQLClient';
import AppError from '../../../app/features/error/services/AppError';
import queryClient from '../../../app/utils/queryClient';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import INotificationMonitoring, {
	INotificationMonitoringItem,
	TNotificationAction,
} from '../interfaces/INotificationMonitoring';

const MONITORING_KEY = 'notificationMonitoring';
const RECIPIENT_KEY = 'notificationMonitoringRecipient';

export const useAdminQueryEnabled = () => {
	const auth = useAuthContext();
	return auth.authContext === 'employee' && auth.isAuthenticated && !auth.authOverrideUserId;
};

export const useNotificationMonitoring = () => {
	const graphQLClient = useGraphQLClient();
	const enabled = useAdminQueryEnabled();

	return useQuery<INotificationMonitoring, Error>(
		[MONITORING_KEY],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query {
							notificationMonitoring {
								emailEnabled
								dryRun
								maxPerDay
								statusCounts {
									status
									count
								}
								planned
								sendableToday
								sentToday
								errorsToday
								lastSendDatetime
								recipients {
									email
									firstName
									lastName
									planned
									sentToday
									remainingQuotaToday
									inError
									oldestPlannedReleaseDate
									estimatedDrainDate
								}
							}
						}
					`,
				)) as { notificationMonitoring: INotificationMonitoring };
				return data.notificationMonitoring;
			} catch (e) {
				throw new AppError(e, 'useNotificationMonitoring');
			}
		},
		// Live data: refetch on demand, errors (e.g. non-admin) shown in the page
		{ enabled, staleTime: 0, useErrorBoundary: false },
	);
};

export const useNotificationMonitoringRecipient = (email: string | null) => {
	const graphQLClient = useGraphQLClient();
	const enabled = useAdminQueryEnabled();

	return useQuery<INotificationMonitoringItem[], Error>(
		[RECIPIENT_KEY, email],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query ($email: String!) {
							notificationMonitoringRecipient(email: $email) {
								id
								notificationId
								documentName
								documentReleaseDate
								subject
								statusTag
								sendStatus
								sendDatetime
								trackingStatus
								trackingStatusDatetime
								rejectReason
								retryable
							}
						}
					`,
					{ email },
				)) as { notificationMonitoringRecipient: INotificationMonitoringItem[] };
				return data.notificationMonitoringRecipient;
			} catch (e) {
				throw new AppError(e, 'useNotificationMonitoringRecipient');
			}
		},
		{ enabled: enabled && !!email, staleTime: 0, useErrorBoundary: false },
	);
};

const MUTATIONS: Record<TNotificationAction, { field: string; query: string }> = {
	send: {
		field: 'sendNotificationNow',
		query: gql`
			mutation ($id: Int!) {
				sendNotificationNow(id: $id) {
					ok
					errorReason
				}
			}
		`,
	},
	resynchronize: {
		field: 'resynchronizeNotification',
		query: gql`
			mutation ($id: Int!) {
				resynchronizeNotification(id: $id) {
					ok
					errorReason
				}
			}
		`,
	},
	delete: {
		field: 'deleteNotification',
		query: gql`
			mutation ($id: Int!) {
				deleteNotification(id: $id) {
					ok
					errorReason
				}
			}
		`,
	},
};

type TNotificationActionResult = { ok: boolean; errorReason: string | null };

export const useNotificationMonitoringAction = () => {
	const graphQLClient = useGraphQLClient();

	const run = async ({ action, id }: { action: TNotificationAction; id: number }) => {
		const { field, query } = MUTATIONS[action];
		const data = (await graphQLClient.request(query, { id })) as Record<string, TNotificationActionResult>;
		return data[field];
	};

	return useMutation(run, {
		onSettled: () => {
			queryClient.invalidateQueries({
				predicate: (query) => query.queryKey[0] === MONITORING_KEY || query.queryKey[0] === RECIPIENT_KEY,
			});
		},
	});
};
